import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { NbAuthJWTToken, NbAuthService, NbAuthToken, NbTokenService } from '@nebular/auth';
import { firstValueFrom } from 'rxjs';
import { RateLimitNotifier } from './rate-limit';

/** Refresh this many ms before the access token expires, so it never reaches the server expired. */
const EXPIRY_MARGIN_MS = 30_000;
/**
 * A forced refresh (the server rejected a token that looks valid) is skipped when a refresh ran
 * this recently: /api/auth/refresh allows only 5 calls per 15 minutes, repeated 401s must not burn them.
 */
const FORCED_REFRESH_COOLDOWN_MS = 60_000;
const REFRESH_LOCK = 'expofin-token-refresh';

/**
 * Keeps the access token valid for every request (REST and GraphQL).
 * An expired, invalid or soon-to-expire token is renewed with POST {auth_path}/api/auth/refresh
 * (the HttpOnly refresh cookie travels thanks to withCredentials). Only one refresh runs at a
 * time, also across browser tabs, and every caller waits for it. It is never retried: if it
 * fails and the token is expired, the session is closed and the user is sent to the login.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private refreshing: Promise<NbAuthJWTToken | null> | null = null;
  private lastRefreshAt = 0;

  // NbAuthService is resolved lazily: it depends on HttpClient, whose interceptors depend on this service.
  constructor(
    private injector: Injector,
    private tokenService: NbTokenService,
    private router: Router,
    private rateLimit: RateLimitNotifier,
  ) {}

  private get authService(): NbAuthService {
    return this.injector.get(NbAuthService);
  }

  /**
   * The current access token, refreshed first when it is expired, invalid or about to expire
   * (or when `force` is set, e.g. after the server rejected it). Null when not logged in.
   */
  async getValidToken(force = false, reason = ''): Promise<NbAuthJWTToken | null> {
    const token = await this.currentToken();
    if (!token?.getValue()) return null;
    const recentlyRefreshed = Date.now() - this.lastRefreshAt < FORCED_REFRESH_COOLDOWN_MS;
    if (this.isUsable(token) && (!force || recentlyRefreshed)) return token as NbAuthJWTToken;
    if (!this.refreshing) {
      console.warn('[TokenRefresh] refreshing access token', { force, reason, valid: token.isValid() });
      this.refreshing = this.withCrossTabLock(() => this.refresh(token.getValue()))
        .finally(() => { this.refreshing = null; });
    }
    return this.refreshing;
  }

  private currentToken(): Promise<NbAuthToken> {
    return firstValueFrom(this.authService.getToken());
  }

  private isUsable(token: NbAuthToken): boolean {
    if (!(token instanceof NbAuthJWTToken) || !token.isValid()) return false;
    const expiry = token.getTokenExpDate();
    return !expiry || expiry.getTime() - Date.now() >= EXPIRY_MARGIN_MS;
  }

  /** Web Locks keep two tabs from refreshing together (the cookie rotates, so the second call would fail). */
  private withCrossTabLock<T>(task: () => Promise<T>): Promise<T> {
    const locks = (navigator as any)?.locks;
    return locks?.request ? locks.request(REFRESH_LOCK, task) : task();
  }

  private async refresh(staleValue: string): Promise<NbAuthJWTToken | null> {
    // Re-read inside the lock: another tab may already have refreshed (tokens are shared via localStorage).
    const token = await this.currentToken();
    if (!token?.getValue()) return null;
    if (token.getValue() !== staleValue && this.isUsable(token)) return token as NbAuthJWTToken;
    this.lastRefreshAt = Date.now();
    try {
      // Same body Nebular sends from isAuthenticatedOrRefresh(): the token itself.
      const result = await firstValueFrom(this.authService.refreshToken(token.getOwnerStrategyName() || 'email', token));
      const fresh = result.isSuccess() ? result.getToken() : null;
      if (fresh instanceof NbAuthJWTToken && fresh.isValid()) return fresh;
      this.rateLimit.handle(result.getResponse());
      throw new Error(result.getErrors()?.join(', ') || 'Refresh token rejected');
    } catch (error) {
      // Only a token that is really expired ends the session: a failed early/forced refresh keeps it.
      if (token instanceof NbAuthJWTToken && token.isValid()) {
        console.warn('[TokenRefresh] refresh failed, keeping the current (still valid) token', error);
        return token;
      }
      console.warn('[TokenRefresh] refresh failed and the token is expired, closing the session', error);
      this.endSession();
      return null;
    }
  }

  private endSession(): void {
    this.tokenService.clear().subscribe();
    if (!this.router.url.startsWith('/auth')) this.router.navigate(['/auth/login']);
  }
}

/** Headers Hasura expects for the given token. */
export function hasuraAuthHeaders(token: NbAuthJWTToken): Record<string, string> {
  const claims = token.getPayload()?.['hasura_claims'] || {};
  return {
    Authorization: `Bearer ${token.getValue()}`,
    'X-Hasura-User-Id': claims['x-hasura-user-id'],
    'X-Hasura-Role': claims['x-hasura-role'],
  };
}
